import React, { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import classes from './dashboard.module.scss';
import AccountHelpers from '../../../utils/accountHelpers';
import { useQuery } from '../../../utils/hooks/useQuery';

const footerDashboardPages = [
  {
    title: 'Sales Reps',
    pages: [
      {
        name: 'Rep scorecards',
        path: '/dashboard/rep-scorecards',
        allowedRoles: ['Admin', 'Rep'],
      },
      {
        name: 'Rep bonus',
        path: '/dashboard/rep-bonus',
        allowedRoles: ['Admin', 'Rep'],
      },
      {
        name: 'User workflow',
        path: '/dashboard/user-workflow',
        allowedRoles: ['Admin'],
      },
      {
        name: 'Note count',
        path: '/dashboard/note-count',
        allowedRoles: ['Admin'],
      },
      {
        name: 'Unassigned physicians',
        path: '/dashboard/unassigned-physicians',
        allowedRoles: ['Admin'],
      },
      {
        name: 'Schedule queue revenue SRQ',
        path: '/dashboard/rep-schedule',
        allowedRoles: ['Admin', 'Rep'],
      },
      {
        name: 'CELMoD Report',
        path: '/dashboard/celmod',
        allowedRoles: ['Admin', 'Rep'],
      },
    ],
  },
  {
    title: 'Movers & Shakers',
    pages: [
      {
        name: 'New physicians',
        path: '/dashboard/new-physicians',
        allowedRoles: ['Admin', 'Rep'],
      },
      {
        name: 'Growth by specializations',
        path: '/dashboard/growth-by-specializations',
        allowedRoles: ['Admin'],
      },
      {
        name: 'SyncPak vs SortPak',
        path: '/dashboard/syncPak-vs-sortPak',
        allowedRoles: ['Admin'],
      },
      {
        name: 'Auto Refill Data',
        path: '/dashboard/autofill-data',
        allowedRoles: ['Admin'],
      },
      {
        name: 'Full Patient List',
        path: '/dashboard/patients',
        allowedRoles: ['Admin'],
      },
      {
        name: 'Pipeline',
        path: '/dashboard/pipeline',
        allowedRoles: ['Admin'],
      },
      {
        name: 'Pipeline Reports',
        path: '/dashboard/pipeline-reports',
        allowedRoles: ['Admin'],
      },
    ],
  },
  {
    title: 'On top',
    firstPages: [
      {
        name: 'Top medications',
        path: '/dashboard/top-medications',
        allowedRoles: ['Admin', 'Rep'],
      },
      {
        name: 'Top cities',
        path: '/dashboard/top-cities',
        allowedRoles: ['Admin', 'Rep'],
      },
      {
        name: 'Top states',
        path: '/dashboard/top-state',
        allowedRoles: ['Admin', 'Rep'],
      },
      {
        name: 'Top specializations',
        path: '/dashboard/top-specializations',
        allowedRoles: ['Admin', 'Rep'],
      },
      {
        name: 'Top copay networks',
        path: '/dashboard/top-copay-networks',
        allowedRoles: ['Admin', 'Rep'],
      },
      {
        name: 'Top physicians',
        path: '/dashboard/top-physicians',
        allowedRoles: ['Admin', 'Rep'],
      },
      {
        name: 'Top physicians by revenue',
        path: '/dashboard/top-physicians-by-revenue',
        allowedRoles: ['Admin', 'Rep'],
      },
    ],
    secondPages: [
      {
        name: 'Top payors',
        path: '/dashboard/top-payors',
        allowedRoles: ['Admin', 'Rep'],
      },
      {
        name: 'Top groups',
        path: '/dashboard/top-groups',
        allowedRoles: ['Admin', 'Rep'],
      },
      {
        name: 'Top hubs',
        path: '/dashboard/top-hubs',
        allowedRoles: ['Admin', 'Rep'],
      },
    ],
  },
];

const footerInDashboardHealth = [
  {
    title: 'Movers & Shakers',
    pages: [
      {
        name: 'SyncPak vs SortPak',
        path: '/dashboard/syncPak-vs-sortPak',
        allowedRoles: ['Health'],
      },
      {
        name: 'Auto Refill Data',
        path: '/dashboard/autofill-data',
        allowedRoles: ['Health'],
      },
      {
        name: 'Full Patient List',
        path: '/dashboard/patients',
        allowedRoles: ['Health'],
      },
    ],
  },
  {
    title: 'On top',
    firstPages: [
      {
        name: 'Top medications',
        path: '/dashboard/top-medications',
        allowedRoles: ['Health'],
      },
      {
        name: 'Top cities',
        path: '/dashboard/top-cities',
        allowedRoles: ['Health'],
      },
      {
        name: 'Top states',
        path: '/dashboard/top-state',
        allowedRoles: ['Health'],
      },
      {
        name: 'Top payors',
        path: '/dashboard/top-payors',
        allowedRoles: ['Health'],
      },
      {
        name: 'Top physicians',
        path: '/dashboard/top-physicians',
        allowedRoles: ['Health'],
      },
    ],
  },

];

const footerInManufacturerUsers = [
  {
    title: 'Movers & Shakers',
    pages: [
      {
        name: 'Script workflow',
        path: '/dashboard/script-workflow',
        allowedRoles: ['Manufacturer Users'],
      },
      {
        name: 'Revenue Growth vs Last Month',
        path: '/dashboard/revenue-growth-last-month',
        allowedRoles: ['Manufacturer Users'],
      },
      {
        name: 'Revenue Growth vs Last Year',
        path: '/dashboard/revenue-growth-last-year',
        allowedRoles: ['Manufacturer Users'],
      },
    ],
  },
  {
    title: 'On top',
    firstPages: [
      {
        name: 'Top medications',
        path: '/dashboard/top-medications',
        allowedRoles: ['Manufacturer Users'],
      },
      {
        name: 'Top cities',
        path: '/dashboard/top-cities',
        allowedRoles: ['Manufacturer Users'],
      },
      {
        name: 'Top states',
        path: '/dashboard/top-state',
        allowedRoles: ['Manufacturer Users'],
      },
      {
        name: 'Top copay networks',
        path: '/dashboard/top-copay-networks',
        allowedRoles: ['Manufacturer Users'],
      },
      {
        name: 'Top physicians',
        path: '/dashboard/top-physicians',
        allowedRoles: ['Manufacturer Users'],
      },
      {
        name: 'Top physicians by revenue',
        path: '/dashboard/top-physicians-by-revenue',
        allowedRoles: ['Manufacturer Users'],
      },
    ],
    secondPages: [
      {
        name: 'Top payors',
        path: '/dashboard/top-payors',
        allowedRoles: ['Manufacturer Users'],
      },
      {
        name: 'Top groups',
        path: '/dashboard/top-groups',
        allowedRoles: ['Manufacturer Users'],
      },
      {
        name: 'Top hubs',
        path: '/dashboard/top-hubs',
        allowedRoles: ['Manufacturer Users'],
      },
    ],
  },

];

const DashboardFooter = () => {
  const { query: { start, end }, queryToStringify } = useQuery();

  const isHealth = AccountHelpers.isInOneOfRoles(['Health']);
  const isManufacturerUsers = AccountHelpers.isInOneOfRoles(['Manufacturer Users']);

  const dashboardPages = useMemo(() => (isManufacturerUsers ? footerInManufacturerUsers : isHealth ? footerInDashboardHealth : footerDashboardPages), []);

  return (
    <div className={`${classes.dashboard_footer_container}  ${isManufacturerUsers ? classes.manufacturer : ''}`}>
      {dashboardPages.map((d) => {
        if (d.title === 'On top') {
          return (
            <div key={d.title} className={`${classes.dashboard_footer_box} ${isHealth ? classes.health : ''}`}>
              <p>{d.title}</p>
              <div className={classes.dashboard_footer_last_box}>
                <div>
                  <ul>
                    {d.firstPages.map((f) => (
                      <li key={f.name}>
                        <NavLink to={{ pathname: f.path, search: queryToStringify({ start, end }) }}>{f.name}</NavLink>
                      </li>
                    ))}
                  </ul>
                </div>

                {!isHealth && (
                <div>
                  <ul>
                    {d.secondPages.map((f) => (
                      <li key={f.name}>
                        <NavLink to={{ pathname: f.path, search: queryToStringify({ start, end }) }}>{f.name}</NavLink>
                      </li>
                    ))}
                  </ul>
                </div>
                )}
              </div>
            </div>
          );
        }

        return (
          <div className={classes.dashboard_footer_box} key={d.title}>
            <p>{d.title}</p>
            <ul>
              {d.pages.map((p) => (
                AccountHelpers.isInOneOfRoles(p.allowedRoles) && (
                  <li key={p.name}>
                    <NavLink to={{ pathname: p.path, search: queryToStringify({ start, end }) }}>{p.name}</NavLink>
                  </li>
                )
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
};

export default DashboardFooter;
